import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GreenButton, RedButton, VioletButton } from '../../components/styled-components';
import { TicketDetails, TicketOption } from '../../modules/tickets/models';
import { getTicketDetails, markTicketAsUsed, rejectTicket } from '../../modules/tickets/operations';
import AnswerEditor from './AnswerEditor';
import InformationDialog from './InformationDialog';
import Grid from '@mui/material/Unstable_Grid2';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../utils/hooks';
import InfoIcon from '@mui/icons-material/Info';

export const HeaderText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    fontSize: '1.125rem',
  },

  fontWeight: 700,
  fontSize: '1.9rem',
  color: '#2B2A2A',
  fontFamily: 'Poppins',
  textAlign: 'center',
}));
export const HeaderColumnText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
  fontWeight: 400,
  fontSize: '1.5rem',
  color: '#2B2A2A',
  fontFamily: 'Poppins',
}));
export const ValueColumnText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    fontSize: '1.125rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#2B2A2A',
  fontFamily: 'Poppins',
}));

export const getFormattedDate = (dateStr: string | undefined): string => {
  if (dateStr) {
    const date = new Date(dateStr);
    return formatInTimeZone(date, 'Europe/Warsaw', 'dd.MM.yyyy HH:mm');
  }
  return '';
};

const ValidTicket = () => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openInfoDialog, setOpenInfoDialog] = useState<boolean>(false);
  const [infoDialogMessage, setInfoDialogMessage] = useState<string>('');
  const [usingTicket, setUsingTicket] = useState<boolean>(false);
  const [openRejectDialog, setOpenRejectDialog] = useState<boolean>(false);
  const theme = useTheme();
  const isMdScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();

  useEffect(() => {
    const GetTicket = async () => {
      if (id) {
        const td = await dispatch(getTicketDetails(id)).unwrap();
        console.log(td);
        setTicket(td);
      }
    };
    GetTicket();
  }, [id]);

  const handleUseTicket = async () => {
    if (!ticket || usingTicket) return;
    setUsingTicket(true);
    const ok = await dispatch(markTicketAsUsed(ticket.uuid)).unwrap();
    setUsingTicket(false);
    setInfoDialogMessage(ok ? t('ticketStatus.ticketUsedMessage') : t('ticketStatus.useTicketErrorMessage'));
    setOpenInfoDialog(true);
    if (ok) {
      setTimeout(() => navigate('/'), 1500);
    }
  };

  // tickets for people with disabilities: staff checks the car and accepts (barrier opened with the remote) or rejects
  const needsStaffApproval = ticket?.requiresStaffApproval === true && ticket.useCount < (ticket.useLimit ?? 1);

  const showResult = (message: string, goHome: boolean) => {
    setInfoDialogMessage(message);
    setOpenInfoDialog(true);
    if (goHome) {
      setTimeout(() => navigate('/'), 2500);
    }
  };

  const handleAcceptTicket = async () => {
    if (!ticket || usingTicket) return;
    setUsingTicket(true);
    const ok = await dispatch(markTicketAsUsed(ticket.uuid)).unwrap();
    setUsingTicket(false);
    showResult(ok ? t('ticketStatus.ticketAcceptedMessage') : t('ticketStatus.useTicketErrorMessage'), ok);
  };

  const handleRejectTicket = async () => {
    setOpenRejectDialog(false);
    if (!ticket || usingTicket) return;
    setUsingTicket(true);
    const result = await dispatch(rejectTicket(ticket.uuid)).unwrap();
    setUsingTicket(false);
    if (!result) {
      showResult(t('ticketStatus.rejectTicketErrorMessage'), false);
    } else if (result.seatReleased) {
      showResult(t('ticketStatus.ticketRejectedMessage'), true);
    } else {
      showResult(t('ticketStatus.ticketRejectedSeatNotReleasedMessage'), true);
    }
  };

  return (
    <>
      <Grid container sx={{ pt: 2 }}>
        <Grid lgOffset={3} lg={6} xs={12} sx={{ backgroundColor: 'white', px: isMdScreen ? 2 : 6, pb: 2 }}>
          <Stack
            direction="column"
            sx={{ width: '100%', alignItems: 'center', mt: isMdScreen ? 0 : 4 }}
            spacing={{ xs: 3, md: 3, lg: 4 }}
          >
            <HeaderText>
              <>{t('ticketStatus.validTicketHeader')}</>
            </HeaderText>
            <Box component="img" src="/images/valid.png" sx={{ height: '20vh' }}></Box>
            {ticket?.variantInternalData != null && ticket.variantInternalData.length > 0 ? (
              <Stack direction={'row'} spacing={2}>
                <InfoIcon />
                <HeaderColumnText>
                  <>{ticket.variantInternalData}</>
                </HeaderColumnText>
              </Stack>
            ) : null}
            {needsStaffApproval && (
              <Stack direction={'row'} spacing={2}>
                <InfoIcon />
                <HeaderColumnText>
                  <>{t('ticketStatus.staffApprovalInfo')}</>
                </HeaderColumnText>
              </Stack>
            )}
            <Box sx={{ alignItems: 'left' }}>
              <Stack direction="row" spacing={2}>
                <HeaderColumnText>
                  <>{t('ticketStatus.ticketIdHeader')}</>:
                </HeaderColumnText>
                <ValueColumnText>{ticket?.ticketId}</ValueColumnText>
              </Stack>
              {ticket?.question != null && (
                <Stack direction="row" spacing={2}>
                  <HeaderColumnText>{ticket?.question?.question}:</HeaderColumnText>
                  <ValueColumnText>{ticket?.question?.answer}</ValueColumnText>
                </Stack>
              )}
              <Stack direction="row" spacing={2}>
                <HeaderColumnText>
                  <>{t('ticketStatus.ticketTypeHeader')}</>:
                </HeaderColumnText>
                <ValueColumnText>{ticket?.variantName}</ValueColumnText>
              </Stack>
              {ticket?.quantity != null && ticket.quantity > 1 && (
                <Stack direction="row" spacing={2}>
                  <HeaderColumnText>
                    <>{t('ticketStatus.ticketQuantityHeader')}</>:
                  </HeaderColumnText>
                  <ValueColumnText>{ticket.quantity}</ValueColumnText>
                </Stack>
              )}
              <Stack direction="row" spacing={2}>
                <HeaderColumnText>
                  <>{t('ticketStatus.validSinceHeader')}</>:
                </HeaderColumnText>
                <ValueColumnText>{getFormattedDate(ticket?.validSince)}</ValueColumnText>
              </Stack>
              <Stack direction="row" spacing={2}>
                <HeaderColumnText>
                  <>{t('ticketStatus.validUntilHeader')}</>:
                </HeaderColumnText>
                <ValueColumnText>{getFormattedDate(ticket?.validUntil)}</ValueColumnText>
              </Stack>
              {ticket?.admissionDates && ticket.admissionDates.length > 0 && (
                <Stack direction="row" spacing={2}>
                  <HeaderColumnText>
                    <>{t('ticketStatus.admissionDateHeader')}</>:
                  </HeaderColumnText>
                  <ValueColumnText>
                    {ticket.admissionDates
                      .map((a) => getFormattedDate(a.date) + (a.description ? ` (${a.description})` : ''))
                      .join(', ')}
                  </ValueColumnText>
                </Stack>
              )}
              <Stack direction="row" spacing={2}>
                <HeaderColumnText>
                  <>{t('ticketStatus.ticketPriceHeder')}</>:
                </HeaderColumnText>
                <ValueColumnText>
                  {ticket?.price} {ticket?.currency}
                </ValueColumnText>
              </Stack>
            </Box>
            {needsStaffApproval && (
              <Stack direction={{ xs: 'row' }} spacing={1}>
                <GreenButton onClick={handleAcceptTicket} disabled={usingTicket}>
                  <>{t('ticketStatus.acceptTicket')}</>
                </GreenButton>
                <RedButton onClick={() => setOpenRejectDialog(true)} disabled={usingTicket}>
                  <>{t('ticketStatus.rejectTicket')}</>
                </RedButton>
              </Stack>
            )}
            <Stack direction={{ xs: 'row' }} spacing={1}>
              {ticket != null && !needsStaffApproval && ticket.ticketOptions?.includes(TicketOption.TicketUse) && (
                <GreenButton onClick={handleUseTicket} disabled={usingTicket}>
                  <>{t('ticketStatus.useTicket')}</>
                </GreenButton>
              )}
              <GreenButton onClick={() => navigate('/')}>
                <>{t('buttons.complete')}</>
              </GreenButton>
              {ticket?.question != null && (
                <VioletButton onClick={() => setOpenDialog(true)}>
                  <>{t('ticketStatus.changeAnswer')}</>
                </VioletButton>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      <AnswerEditor handleClose={() => setOpenDialog(false)} open={openDialog} ticket={ticket}></AnswerEditor>
      <Dialog onClose={() => setOpenRejectDialog(false)} open={openRejectDialog}>
        <DialogContent>
          <>{t('ticketStatus.rejectConfirmation')}</>
        </DialogContent>
        <DialogActions sx={{ py: 3, justifyContent: 'center' }}>
          <VioletButton onClick={() => setOpenRejectDialog(false)}>
            <>{t('buttons.close')}</>
          </VioletButton>
          <RedButton onClick={handleRejectTicket} disabled={usingTicket}>
            <>{t('ticketStatus.rejectTicket')}</>
          </RedButton>
        </DialogActions>
      </Dialog>
      <InformationDialog
        handleClose={() => setOpenInfoDialog(false)}
        message={infoDialogMessage}
        open={openInfoDialog}
      />
    </>
  );
};

export default ValidTicket;
